/**
 * KDS Thunks
 *
 * Read-only thunks call the local SQLite functions directly (Category A
 * convention — see localKdsOps.ts), same as sectionThunks.ts's
 * fetchSections/fetchTables. The status-transition write is also a direct
 * local call, analogous to KOT confirmation (also Category A).
 */

import { LocalOrderContext } from '@/renderer/lib/powersync/localOrderOps';
import {
  localFetchDepartmentsForKds,
  localFetchKdsBoard,
  localFetchKdsKpis,
  localTransitionKitchenTicket,
} from '@/renderer/lib/powersync/localKdsOps';
import {
  KdsDepartmentOption,
  KdsKpis,
  KdsTicket,
  KitchenTicketStatus,
} from '@/renderer/types/kds';
import { createAsyncThunk } from '@reduxjs/toolkit';

import { KdsThunkState } from './kdsTypes';

const EMPTY_KPIS: KdsKpis = {
  active_orders: 0,
  average_time_minutes: 0,
  completed_orders: 0,
  delayed_orders: 0,
};

const buildLocalCtx = (state: KdsThunkState): LocalOrderContext => {
  const { user } = state.auth;
  const firstName = user?.first_name || '';
  const lastName = user?.last_name || '';
  const fullName = `${firstName} ${lastName}`.trim() || user?.email || 'Staff';

  return {
    outletId: user?.outlet?.id || '',
    outletName: user?.outlet?.name || '',
    companyId: user?.company?.id ?? 0,
    userId: user?.id || '',
    userName: fullName,
    isKdsEnabled: Boolean(user?.outlet?.is_kds_enabled),
  };
};

export const fetchKdsDepartments = createAsyncThunk<
  KdsDepartmentOption[],
  void,
  { state: KdsThunkState; rejectValue: string }
>('kds/fetchDepartments', async (_arg, { getState, rejectWithValue }) => {
  const ctx = buildLocalCtx(getState());
  if (!ctx.outletId) return [];
  const res = await localFetchDepartmentsForKds(ctx.outletId);
  if (!res.success) {
    return rejectWithValue(res.error || 'Failed to load departments');
  }
  return res.data ?? [];
});

export const fetchKdsBoard = createAsyncThunk<
  { tickets: KdsTicket[]; kpis: KdsKpis },
  string,
  { state: KdsThunkState; rejectValue: string }
>('kds/fetchBoard', async (departmentId, { getState, rejectWithValue }) => {
  const ctx = buildLocalCtx(getState());
  if (!ctx.outletId || !departmentId) {
    return { tickets: [], kpis: EMPTY_KPIS };
  }

  const [boardRes, kpiRes] = await Promise.all([
    localFetchKdsBoard(departmentId, ctx),
    localFetchKdsKpis(departmentId, ctx),
  ]);

  if (!boardRes.success) {
    return rejectWithValue(boardRes.error || 'Failed to load KDS board');
  }

  return { tickets: boardRes.data ?? [], kpis: kpiRes.data ?? EMPTY_KPIS };
});

export const transitionKdsTicket = createAsyncThunk<
  void,
  { ticketId: string; toStatus: KitchenTicketStatus; departmentId: string },
  { state: KdsThunkState; rejectValue: string }
>(
  'kds/transitionTicket',
  async (
    { ticketId, toStatus, departmentId },
    { getState, dispatch, rejectWithValue },
  ) => {
    const ctx = buildLocalCtx(getState());
    const res = await localTransitionKitchenTicket(ticketId, toStatus, ctx);
    if (!res.success) {
      // The optimistic update (kdsSlice's transitionKdsTicket.pending case)
      // already moved this ticket in the UI — resync from real SQLite state
      // on failure too, not just on success, so it snaps back correctly.
      await dispatch(fetchKdsBoard(departmentId));
      return rejectWithValue(res.error || 'Failed to update ticket');
    }
    await dispatch(fetchKdsBoard(departmentId));
    return undefined;
  },
);
